/**
 * Frontend leverage options utility that aligns with backend validation
 * This mirrors the backend LeverageOption enum and validation logic
 */

export interface LeverageOption {
    value: number;
    label: string;
    riskLevel: 'conservative' | 'standard' | 'aggressive';
    description: string;
}

// Standard leverage options matching backend LeverageOption enum
export const LEVERAGE_OPTIONS: LeverageOption[] = [
    {
        value: 10,
        label: '1:10',
        riskLevel: 'conservative',
        description: 'Conservative - Low risk, suitable for beginners',
    },
    {
        value: 20,
        label: '1:20',
        riskLevel: 'conservative',
        description: 'Conservative - Low risk, suitable for beginners',
    },
    {
        value: 50,
        label: '1:50',
        riskLevel: 'standard',
        description: 'Standard - Balanced risk and reward',
    },
    {
        value: 100,
        label: '1:100',
        riskLevel: 'standard',
        description: 'Standard - Balanced risk and reward',
    },
    {
        value: 200,
        label: '1:200',
        riskLevel: 'aggressive',
        description: 'Aggressive - High risk, experienced traders only',
    },
    {
        value: 500,
        label: '1:500',
        riskLevel: 'aggressive',
        description: 'Very aggressive - Maximum risk, professional traders only',
    },
    {
        value: 1000,
        label: '1:1000',
        riskLevel: 'aggressive',
        description: 'Extreme - Very high risk, advanced traders only',
    },
    {
        value: 2000,
        label: '1:2000',
        riskLevel: 'aggressive',
        description: 'Extreme - Maximum risk, expert traders only',
    },
];

// Instrument type definitions
export type InstrumentType = 'forex' | 'crypto' | 'stock' | 'commodity';

// Account type definitions
export type AccountType = 'demo' | 'retail' | 'professional' | 'institutional';

/**
 * Get leverage options for a specific instrument type
 */
export function getLeverageOptionsForInstrument(
    instrumentType: InstrumentType,
    accountType: AccountType = 'retail'
): LeverageOption[] {
    // Get all leverage options for the instrument type
    const instrumentOptions = getLeverageOptionsByInstrumentType(instrumentType);

    // Filter by account type limits
    const maxAccountLeverage = getMaxLeverageForAccountType(accountType);

    return instrumentOptions.filter(option => option.value <= maxAccountLeverage);
}

/**
 * Get leverage options by instrument type (mirrors backend logic)
 */
export function getLeverageOptionsByInstrumentType(
    instrumentType: InstrumentType
): LeverageOption[] {
    switch (instrumentType) {
        case 'forex':
            // Forex supports all leverage options up to 1:2000
            return LEVERAGE_OPTIONS;
        case 'crypto':
            // Crypto limited to 1:100 for risk management
            return LEVERAGE_OPTIONS.filter(option => option.value <= 100);
        case 'stock':
            // Stocks limited to 1:50 for regulatory compliance
            return LEVERAGE_OPTIONS.filter(option => option.value <= 50);
        case 'commodity':
            // Commodities limited to 1:100 for volatility management
            return LEVERAGE_OPTIONS.filter(option => option.value <= 100);
        default:
            return LEVERAGE_OPTIONS.filter(option => option.value <= 100);
    }
}

/**
 * Get maximum leverage for account type (mirrors backend logic)
 */
export function getMaxLeverageForAccountType(accountType: AccountType): number {
    switch (accountType) {
        case 'demo':
            return 2000; // Demo accounts can use any leverage
        case 'retail':
            return 200; // Retail accounts limited to 1:200 (ESMA regulations)
        case 'professional':
            return 2000; // Professional accounts can use higher leverage
        case 'institutional':
            return 2000; // Institutional accounts can use any leverage
        default:
            return 200; // Default to retail limits
    }
}

/**
 * Check if a leverage value is valid for a specific instrument type
 */
export function isValidLeverageForInstrument(
    leverage: number,
    instrumentType: InstrumentType
): boolean {
    const validOptions = getLeverageOptionsByInstrumentType(instrumentType);
    return validOptions.some(option => option.value === leverage);
}

/**
 * Get recommended leverage for beginners by instrument type
 */
export function getRecommendedLeverageForBeginners(instrumentType: InstrumentType): number {
    switch (instrumentType) {
        case 'forex':
            return 50; // 1:50 for forex beginners
        case 'crypto':
            return 20; // 1:20 for crypto beginners
        case 'stock':
            return 10; // 1:10 for stock beginners
        case 'commodity':
            return 20; // 1:20 for commodity beginners
        default:
            return 20;
    }
}

/**
 * Get leverage options for select dropdowns
 */
export function getLeverageSelectOptions(
    instrumentType: InstrumentType,
    accountType: AccountType = 'retail'
): { value: string; label: string; description?: string }[] {
    const options = getLeverageOptionsForInstrument(instrumentType, accountType);

    return options.map(option => ({
        value: option.value.toString(),
        label: option.label,
        description: option.description,
    }));
}

/**
 * Get leverage options grouped by risk level
 */
export function getLeverageOptionsByRiskLevel(
    instrumentType: InstrumentType,
    accountType: AccountType = 'retail'
): {
    conservative: LeverageOption[];
    standard: LeverageOption[];
    aggressive: LeverageOption[];
} {
    const options = getLeverageOptionsForInstrument(instrumentType, accountType);

    return {
        conservative: options.filter(option => option.riskLevel === 'conservative'),
        standard: options.filter(option => option.riskLevel === 'standard'),
        aggressive: options.filter(option => option.riskLevel === 'aggressive'),
    };
}

/**
 * Get default leverage for an instrument type
 */
export function getDefaultLeverage(instrumentType: InstrumentType): number {
    switch (instrumentType) {
        case 'forex':
            return 100; // 1:100 default for forex
        case 'crypto':
            return 20; // 1:20 default for crypto
        case 'stock':
            return 10; // 1:10 default for stocks
        case 'commodity':
            return 20; // 1:20 default for commodities
        default:
            return 50;
    }
}

/**
 * Maximum leverage allowed for an order: min(instrument max, account leverage).
 */
export function getMaxAllowedOrderLeverage(
    instrumentMaxLeverage: number,
    accountLeverage: number
): number {
    return Math.max(1, Math.min(instrumentMaxLeverage, accountLeverage));
}

/**
 * Default order leverage: the highest allowed for this instrument/account pair.
 */
export function getOrderLeverage(instrumentMaxLeverage: number, accountLeverage: number): number {
    return getMaxAllowedOrderLeverage(instrumentMaxLeverage, accountLeverage);
}

/**
 * Leverage choices for the order form dropdown, capped by instrument/account limits.
 */
export function getOrderLeverageSelectOptions(
    maxAllowedLeverage: number,
    defaultLeverage: number
): { value: string; label: string }[] {
    const values = new Set<number>();

    for (const option of LEVERAGE_OPTIONS) {
        if (option.value <= maxAllowedLeverage) {
            values.add(option.value);
        }
    }

    values.add(Math.max(1, Math.min(defaultLeverage, maxAllowedLeverage)));
    values.add(Math.max(1, maxAllowedLeverage));

    return Array.from(values)
        .sort((a, b) => a - b)
        .map(value => ({
            value: value.toString(),
            label: `1:${value}`,
        }));
}

/**
 * Clamp a selected leverage value to the allowed order range.
 */
export function clampOrderLeverage(selectedLeverage: number, maxAllowedLeverage: number): number {
    return Math.max(1, Math.min(selectedLeverage, maxAllowedLeverage));
}
